// BUILD-042 (Oct 24) - small shared pieces for the guided-setup step screens.
import { useState } from 'react';
import { unavailableText } from './setupModel';

export const PRIMARY_BTN = 'max-w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium bg-[#4AABDE] text-white rounded-md px-4 py-2 hover:bg-[#3b96c4] disabled:opacity-50 disabled:cursor-not-allowed';
export const OUTLINE_BTN = 'max-w-full whitespace-normal text-left inline-flex items-center justify-center gap-1.5 text-sm font-medium border border-gray-300 bg-white text-gray-700 rounded-md px-4 py-2 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed';

/** An area the caller cannot read. It is never "empty" and never "not applicable"; the step cannot be completed from this account. */
export function UnavailableNotice({ title, reason, capability }: { title: string; reason: string; capability: string | null }) {
  return (
    <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="area-unavailable">
      <div className="font-semibold">{title} can’t be shown with your permissions</div>
      <p className="mt-1 text-xs">
        {unavailableText(reason, capability)} Unseen information is never treated as empty or as “not applicable”, so this step can’t be completed from this account
        {capability ? <> and HR setup can’t be finished until someone who holds <code className="font-mono">{capability}</code> reviews it.</> : '.'}
      </p>
    </div>
  );
}

/** "No <thing> applies": a review statement only. Offered only when nothing is assigned and the area is readable; the server re-checks. */
export function NotApplicableCard({ label, helper, blockedReason, decided, onDecide, onClear, testId }: {
  label: string;
  helper: string;
  /** When set, the statement is unavailable and this says why (something is already assigned). */
  blockedReason: string | null;
  decided: boolean;
  onDecide: () => Promise<{ ok: true } | { ok: false; message: string }>;
  onClear: () => Promise<{ ok: true } | { ok: false; message: string }>;
  testId: string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function run(fn: () => Promise<{ ok: true } | { ok: false; message: string }>) {
    if (busy) return;
    setBusy(true); setErr(null);
    const r = await fn();
    if (!r.ok) setErr(`Not saved. ${r.message}`);
    setBusy(false);
  }
  return (
    <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 px-4 py-3" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900">{decided ? `${label} — recorded` : label}</div>
          <p className="text-xs text-gray-500 mt-0.5">{blockedReason ?? helper}</p>
        </div>
        {!blockedReason && !decided && <button type="button" className={OUTLINE_BTN} disabled={busy} onClick={() => run(onDecide)} data-testid={`${testId}-record`}>{busy ? 'Saving…' : label}</button>}
        {decided && <button type="button" className="text-xs text-[#176A9B] underline" disabled={busy} onClick={() => run(onClear)} data-testid={`${testId}-clear`}>Undo</button>}
      </div>
      {err && <p role="alert" className="mt-2 text-xs text-red-700" data-testid={`${testId}-error`}>{err}</p>}
    </div>
  );
}

export function StepCard({ title, subtitle, testId, children }: { title: string; subtitle?: string; testId: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 sm:p-6" data-testid={testId}>
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5 mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}
